import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) return logoUrl;
  return `${API}/files/${logoUrl}`;
};

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// ─── Shared Form Components ───
const GoldInput = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm text-white/60 mb-2">{label}{required && <span className="text-[#C5A059] ml-1">*</span>}</label>
    <input {...props} className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#C5A059] focus:outline-none transition-colors placeholder:text-white/20" />
  </div>
);

const GoldButton = ({ children, variant = 'primary', ...props }) => {
  const base = variant === 'primary' ? 'bg-[#C5A059] text-black hover:bg-[#E2C382]' : 'border border-white/20 text-white hover:border-white/60';
  return <button {...props} className={`px-8 py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-50 ${base} ${props.className || ''}`}>{children}</button>;
};

const ServiceInputField = ({ services, onAdd, onRemove }) => {
  const [input, setInput] = useState('');
  const add = () => { if (input.trim()) { onAdd(input.trim()); setInput(''); } };
  return (
    <div>
      <label className="block text-sm text-white/60 mb-2">Services Offered</label>
      <div className="flex gap-2 mb-2">
        <input value={input} onChange={e => setInput(e.target.value)} className="flex-1 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:border-[#C5A059] focus:outline-none" placeholder="Add a service..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
        <GoldButton type="button" onClick={add} className="px-4 py-2">Add</GoldButton>
      </div>
      <div className="flex flex-wrap gap-2">
        {services.map((s, i) => (
          <span key={`svc-${s}-${i}`} className="bg-white/5 border border-white/10 px-3 py-1 text-xs text-white/60 flex items-center gap-2">
            {s} <button type="button" onClick={() => onRemove(i)} className="text-red-400 hover:text-red-300">&times;</button>
          </span>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// ADMIN CATEGORIES
// ═══════════════════════════════════════════

export const AdminCategories = () => {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name: '', description: '' });
  const [editing, setEditing] = useState(null);

  const fetchCats = useCallback(() => {
    axios.get(`${API}/categories`, { withCredentials: true }).then(({ data }) => setCats(data)).catch(() => {});
  }, []);
  useEffect(() => { fetchCats(); }, [fetchCats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await axios.put(`${API}/admin/categories/${editing}`, form, { withCredentials: true });
    } else {
      await axios.post(`${API}/admin/categories`, form, { withCredentials: true });
    }
    setForm({ name: '', description: '' });
    setEditing(null);
    fetchCats();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/admin/categories/${id}`, { withCredentials: true });
    fetchCats();
  };

  return (
    <div data-testid="admin-categories-section">
      <form onSubmit={handleSubmit} className="bg-[#0F0F14] border border-white/[0.04] p-6 mb-6 space-y-4">
        <h3 className="text-lg text-white/80 mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>{editing ? 'Edit Category' : 'Add Category'}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <GoldInput data-testid="cat-name-input" label="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <GoldInput data-testid="cat-desc-input" label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="flex gap-3">
          <GoldButton data-testid="cat-submit-btn" type="submit">{editing ? 'Update' : 'Add'}</GoldButton>
          {editing && <GoldButton variant="secondary" type="button" onClick={() => { setEditing(null); setForm({ name: '', description: '' }); }}>Cancel</GoldButton>}
        </div>
      </form>
      <div className="space-y-3" data-testid="categories-list">
        {cats.map((c, i) => (
          <div key={c.id} className="bg-[#0F0F14] border border-white/[0.04] p-4 flex items-center justify-between" data-testid={`category-item-${i}`}>
            <div><div className="text-white text-sm font-medium">{c.name}</div><div className="text-white/40 text-xs">{c.description}</div></div>
            <div className="flex gap-2">
              <button data-testid={`cat-edit-${i}`} onClick={() => { setEditing(c.id); setForm({ name: c.name, description: c.description || '' }); }} className="text-[#C5A059] text-xs hover:text-[#E2C382]">Edit</button>
              <button data-testid={`cat-delete-${i}`} onClick={() => handleDelete(c.id)} className="text-red-400 text-xs hover:text-red-300">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// ADMIN PROVIDERS
// ═══════════════════════════════════════════

export const AdminProviders = () => {
  const [providers, setProviders] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name: '', logo_url: '', specialty: '', category_id: '', discount_percent: 0, city: '', state: '', location: '', contact_person_number: '', email: '', website: '', bio: '', services_offered: [] });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchProviders = useCallback((q = '') => {
    axios.get(`${API}/admin/providers?search=${encodeURIComponent(q)}`, { withCredentials: true }).then(({ data }) => setProviders(data)).catch(() => {});
  }, []);
  const fetchCats = useCallback(() => {
    axios.get(`${API}/categories`, { withCredentials: true }).then(({ data }) => setCats(data)).catch(() => {});
  }, []);
  useEffect(() => { fetchProviders(); fetchCats(); }, [fetchProviders, fetchCats]);

  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => fetchProviders(val), 300));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, discount_percent: parseFloat(form.discount_percent) || 0 };
    if (editing) { await axios.put(`${API}/admin/providers/${editing}`, payload, { withCredentials: true }); }
    else { await axios.post(`${API}/admin/providers`, payload, { withCredentials: true }); }
    resetForm();
    fetchProviders(search);
  };

  const resetForm = () => {
    setForm({ name: '', logo_url: '', specialty: '', category_id: '', discount_percent: 0, city: '', state: '', location: '', contact_person_number: '', email: '', website: '', bio: '', services_offered: [] });
    setEditing(null);
  };

  const handleDelete = async (id) => { await axios.delete(`${API}/admin/providers/${id}`, { withCredentials: true }); fetchProviders(search); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="admin-providers-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg text-white/80" style={{ fontFamily: 'Cormorant Garamond' }}>Service Provider Directory</h3>
        <GoldButton data-testid="download-providers-btn" onClick={() => window.open(`${API}/admin/providers/download`, '_blank')}>Download CSV</GoldButton>
      </div>
      <div className="mb-6">
        <input data-testid="provider-search" value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search by name, specialty, city, state, category, phone, SP ID, or email..." className="w-full bg-[#0F0F14] border border-white/10 py-3 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none placeholder:text-white/30" />
      </div>
      <form onSubmit={handleSubmit} className="bg-[#0F0F14] border border-white/[0.04] p-6 mb-6 space-y-4">
        <h3 className="text-lg text-white/80 mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>{editing ? 'Edit Provider' : 'Add Service Provider'}</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <GoldInput data-testid="prov-name" label="Name" value={form.name} onChange={set('name')} required />
          <GoldInput data-testid="prov-specialty" label="Specialty" value={form.specialty} onChange={set('specialty')} required />
          <div>
            <label className="block text-sm text-white/60 mb-2">Category <span className="text-[#C5A059]">*</span></label>
            <select data-testid="prov-category" value={form.category_id} onChange={set('category_id')} className="w-full bg-[#0F0F14] border border-white/20 py-3 px-3 text-white focus:border-[#C5A059] focus:outline-none text-sm" required>
              <option value="">Select</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <GoldInput data-testid="prov-city" label="City" value={form.city} onChange={set('city')} required />
          <GoldInput data-testid="prov-state" label="State" value={form.state} onChange={set('state')} />
          <GoldInput data-testid="prov-location" label="Location" value={form.location} onChange={set('location')} />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <GoldInput data-testid="prov-logo" label="Logo URL" value={form.logo_url} onChange={set('logo_url')} />
          <GoldInput data-testid="prov-discount" label="Discount %" type="number" value={form.discount_percent} onChange={set('discount_percent')} />
          <GoldInput data-testid="prov-contact" label="Contact Person Number" value={form.contact_person_number} onChange={set('contact_person_number')} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <GoldInput data-testid="prov-email" label="Email" value={form.email} onChange={set('email')} />
          <GoldInput data-testid="prov-website" label="Website" value={form.website} onChange={set('website')} />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-2">Bio <span className="text-white/30">(max 150 characters)</span></label>
          <textarea data-testid="prov-bio" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value.slice(0, 150) })} maxLength={150} className="w-full bg-transparent border border-white/20 py-3 px-4 text-white focus:border-[#C5A059] focus:outline-none h-16 resize-none text-sm" />
          <div className={`text-xs text-right mt-1 ${(form.bio || '').length >= 150 ? 'text-red-400' : 'text-white/30'}`} data-testid="prov-bio-count">{(form.bio || '').length}/150</div>
        </div>
        <ServiceInputField services={form.services_offered} onAdd={s => setForm({...form, services_offered: [...form.services_offered, s]})} onRemove={idx => setForm({...form, services_offered: form.services_offered.filter((_, i) => i !== idx)})} />
        <div className="flex gap-3">
          <GoldButton data-testid="prov-submit-btn" type="submit">{editing ? 'Update' : 'Add Provider'}</GoldButton>
          {editing && <GoldButton variant="secondary" type="button" onClick={resetForm}>Cancel</GoldButton>}
        </div>
      </form>
      <div className="space-y-3" data-testid="providers-list">
        {providers.map((p, i) => (
          <div key={p.id} className="bg-[#0F0F14] border border-white/[0.04] p-4" data-testid={`admin-provider-${i}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                {p.logo_url && <img src={resolveLogoUrl(p.logo_url)} alt={`${p.name} logo`} className="w-16 h-16 object-cover border border-white/10 rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                <div>
                  <div className="text-white text-sm font-medium">{p.name}</div>
                  <div className="text-[#C5A059] text-xs">{p.specialty}</div>
                  <div className="text-white/40 text-xs">{p.city}{p.state ? `, ${p.state}` : ''}{p.location ? ` - ${p.location}` : ''} | {p.category_name}</div>
                  <div className="text-white/40 text-xs mt-1" data-testid={`admin-provider-phone-${i}`}>Phone: {p.contact_person_number || p.owner_phone || '—'}</div>
                  {p.discount_percent > 0 && <div className="text-[#C5A059] text-xs mt-1">{p.discount_percent}% Discount</div>}
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`prov-edit-${i}`} onClick={() => { setEditing(p.id); setForm({ name: p.name||'', logo_url: p.logo_url||'', specialty: p.specialty||'', category_id: p.category_id||'', discount_percent: p.discount_percent||0, city: p.city||'', state: p.state||'', location: p.location||'', contact_person_number: p.contact_person_number||'', email: p.email||'', website: p.website||'', bio: p.bio||'', services_offered: p.services_offered||[] }); }} className="text-[#C5A059] text-xs hover:text-[#E2C382]">Edit</button>
                <button data-testid={`prov-delete-${i}`} onClick={() => { if (window.confirm(`Delete listing for ${p.name}? Only the listing will be removed, the provider profile stays.`)) handleDelete(p.id); }} className="text-red-400 text-xs hover:text-red-300">Delete Listing</button>
                {p.owner_id && <button data-testid={`prov-delete-profile-${i}`} onClick={async () => { if (window.confirm(`Delete provider profile for ${p.name}? This removes their account, listing, and all data permanently.`)) { await axios.delete(`${API}/admin/provider-profile/${p.owner_id}`, { withCredentials: true }); fetch(); } }} className="text-red-400 text-xs hover:text-red-300 border border-red-400/30 px-2 py-0.5">Delete Profile</button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// ADMIN MEMBERS
// ═══════════════════════════════════════════

export const AdminMembers = () => {
  const [members, setMembers] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', city: '', state: '', phone: '', bio: '', is_verified: false });
  const [addError, setAddError] = useState('');
  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchMembers = useCallback((q = '') => {
    axios.get(`${API}/admin/members?search=${encodeURIComponent(q)}`, { withCredentials: true }).then(({ data }) => setMembers(data)).catch(() => {});
  }, []);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => fetchMembers(val), 300));
  };

  const handleUpdate = async (e) => { e.preventDefault(); await axios.put(`${API}/admin/members/${editingMember}`, editForm, { withCredentials: true }); setEditingMember(null); fetchMembers(search); };

  const handleAddMember = async (e) => {
    e.preventDefault(); setAddError('');
    try { await axios.post(`${API}/admin/members`, addForm, { withCredentials: true }); setShowAddForm(false); setAddForm({ name: '', email: '', password: '', city: '', state: '', phone: '', bio: '', is_verified: false }); fetchMembers(search); }
    catch (err) { setAddError(formatError(err.response?.data?.detail)); }
  };

  const handleRevokeFreeAccess = async (memberId) => {
    await axios.put(`${API}/admin/members/${memberId}`, { free_access: false, free_access_until: null }, { withCredentials: true });
    fetchMembers(search);
  };

  return (
    <div data-testid="admin-members-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg text-white/80" style={{ fontFamily: 'Cormorant Garamond' }}>Member Directory ({members.length})</h3>
        <div className="flex gap-3">
          <GoldButton data-testid="add-member-btn" onClick={() => setShowAddForm(!showAddForm)}>{showAddForm ? 'Cancel' : 'Add Member'}</GoldButton>
          <GoldButton data-testid="download-members-btn" onClick={() => window.open(`${API}/admin/members/download`, '_blank')}>Download CSV</GoldButton>
        </div>
      </div>
      <div className="mb-6">
        <input data-testid="member-search" value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search by name, email, city, state, phone, or referral code..." className="w-full bg-[#0F0F14] border border-white/10 py-3 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none placeholder:text-white/30" />
      </div>

      {showAddForm && (
        <form onSubmit={handleAddMember} className="bg-[#0F0F14] border border-[#C5A059]/30 p-6 mb-6 space-y-4">
          <h3 className="text-lg text-white/80" style={{ fontFamily: 'Cormorant Garamond' }}>Add New Member (No Referral Required)</h3>
          {addError && <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{addError}</div>}
          <div className="grid md:grid-cols-3 gap-4">
            <GoldInput data-testid="add-member-name" label="Name" value={addForm.name} onChange={e => setAddForm({...addForm, name: e.target.value})} required />
            <GoldInput data-testid="add-member-email" label="Email" type="email" value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} required />
            <GoldInput data-testid="add-member-password" label="Password" type="password" value={addForm.password} onChange={e => setAddForm({...addForm, password: e.target.value})} required />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <GoldInput data-testid="add-member-city" label="City" value={addForm.city} onChange={e => setAddForm({...addForm, city: e.target.value})} required />
            <GoldInput data-testid="add-member-state" label="State" value={addForm.state} onChange={e => setAddForm({...addForm, state: e.target.value})} required />
            <GoldInput data-testid="add-member-phone" label="Phone" value={addForm.phone} onChange={e => setAddForm({...addForm, phone: e.target.value})} required />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="block text-sm text-white/60 mb-2">Bio</label><textarea data-testid="add-member-bio" value={addForm.bio} onChange={e => setAddForm({...addForm, bio: e.target.value})} className="w-full bg-transparent border border-white/20 py-3 px-4 text-white focus:border-[#C5A059] focus:outline-none h-16 resize-none text-sm" /></div>
            <div><label className="block text-sm text-white/60 mb-2">Verified</label><select data-testid="add-member-verified" value={addForm.is_verified ? 'true' : 'false'} onChange={e => setAddForm({...addForm, is_verified: e.target.value === 'true'})} className="w-full bg-[#0F0F14] border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none"><option value="false">Not Verified</option><option value="true">Verified</option></select></div>
          </div>
          <GoldButton data-testid="add-member-submit" type="submit">Create Member</GoldButton>
        </form>
      )}

      {editingMember && (
        <form onSubmit={handleUpdate} className="bg-[#0F0F14] border border-[#C5A059]/30 p-6 mb-6 space-y-4">
          <h3 className="text-lg text-white/80" style={{ fontFamily: 'Cormorant Garamond' }}>Edit Member</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <GoldInput data-testid="member-edit-name" label="Name" value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
            <GoldInput data-testid="member-edit-city" label="City" value={editForm.city || ''} onChange={e => setEditForm({ ...editForm, city: e.target.value })} />
            <GoldInput data-testid="member-edit-state" label="State" value={editForm.state || ''} onChange={e => setEditForm({ ...editForm, state: e.target.value })} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <GoldInput data-testid="member-edit-phone" label="Phone" value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
            <div><label className="block text-sm text-white/60 mb-2">Verified</label><select data-testid="member-edit-verified" value={editForm.is_verified ? 'true' : 'false'} onChange={e => setEditForm({ ...editForm, is_verified: e.target.value === 'true' })} className="w-full bg-[#0F0F14] border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none"><option value="true">Verified</option><option value="false">Not Verified</option></select></div>
          </div>
          <div><label className="block text-sm text-white/60 mb-2">Free Access</label>
            <div className="space-y-3">
              <select data-testid="member-edit-free-access" value={editForm.free_access ? 'true' : 'false'} onChange={e => setEditForm({ ...editForm, free_access: e.target.value === 'true' })} className="w-full bg-[#0F0F14] border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none"><option value="false">No (Requires Subscription)</option><option value="true">Yes (Free Access)</option></select>
              {editForm.free_access && (
                <div>
                  <label className="block text-xs text-white/40 mb-1">Free Access Until (leave empty for unlimited)</label>
                  <input data-testid="member-edit-free-until" type="date" value={editForm.free_access_until || ''} onChange={e => setEditForm({ ...editForm, free_access_until: e.target.value || null })} className="w-full bg-[#0F0F14] border border-white/20 py-2 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none" />
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3"><GoldButton data-testid="member-update-btn" type="submit">Update</GoldButton><GoldButton variant="secondary" type="button" onClick={() => setEditingMember(null)}>Cancel</GoldButton></div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="members-table">
          <thead><tr className="border-b border-white/[0.08]"><th className="text-left py-3 text-white/60 font-medium">Name</th><th className="text-left py-3 text-white/60 font-medium">Email</th><th className="text-left py-3 text-white/60 font-medium">Phone</th><th className="text-left py-3 text-white/60 font-medium">City</th><th className="text-left py-3 text-white/60 font-medium">Access</th><th className="text-left py-3 text-white/60 font-medium">Actions</th></tr></thead>
          <tbody>
            {members.map((m, i) => (
              <tr key={m.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`member-row-${i}`}>
                <td className="py-3"><div className="text-white">{m.name}</div><div className="text-white/30 text-xs">{m.referral_code}</div></td>
                <td className="py-3 text-white/60 text-xs">{m.email}</td>
                <td className="py-3 text-white/60 text-xs" data-testid={`member-phone-${i}`}>{m.phone || '—'}</td>
                <td className="py-3 text-white/60 text-xs">{m.city}</td>
                <td className="py-3">{m.free_access ? (
                  <div>
                    <span className="text-green-400 text-xs">Free Access</span>
                    {m.free_access_until && <div className="text-white/30 text-[10px]">until {m.free_access_until.split('T')[0]}</div>}
                    <button data-testid={`revoke-free-${i}`} onClick={() => handleRevokeFreeAccess(m.id)} className="text-red-400/60 text-[10px] hover:text-red-400 block mt-0.5">Revoke</button>
                  </div>
                ) : <span className="text-white/30 text-xs">Subscription</span>}</td>
                <td className="py-3">
                  <button data-testid={`member-edit-btn-${i}`} onClick={() => { setEditingMember(m.id); setEditForm({ name: m.name, city: m.city, state: m.state, phone: m.phone, is_verified: m.is_verified, free_access: m.free_access || false, free_access_until: m.free_access_until ? m.free_access_until.split('T')[0] : '' }); }} className="text-[#C5A059] text-xs hover:text-[#E2C382]">Edit</button>
                  <button data-testid={`member-delete-btn-${i}`} onClick={async () => { if (window.confirm(`Delete member ${m.name}? This cannot be undone.`)) { await axios.delete(`${API}/admin/members/${m.id}`, { withCredentials: true }); fetchMembers(search); } }} className="text-red-400 text-xs hover:text-red-300 ml-3">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// ADMIN SUBSCRIPTIONS
// ═══════════════════════════════════════════

export const AdminSubscriptions = () => {
  const [subs, setSubs] = useState([]);
  const [processing, setProcessing] = useState('');
  const [filterType, setFilterType] = useState('all');

  const fetchSubs = useCallback(() => {
    axios.get(`${API}/admin/subscriptions`, { withCredentials: true }).then(({ data }) => setSubs(data)).catch(() => {});
  }, []);
  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const markPaid = async (subId) => { setProcessing(subId); try { await axios.put(`${API}/admin/subscriptions/${subId}`, { status: 'active', notes: 'Marked paid manually by admin' }, { withCredentials: true }); fetchSubs(); } catch (err) { console.error('Mark paid failed:', err.message); } setProcessing(''); };
  const updateStatus = async (subId, status) => { await axios.put(`${API}/admin/subscriptions/${subId}`, { status }, { withCredentials: true }); fetchSubs(); };
  const deleteSub = async (subId, name) => { if (window.confirm(`Delete subscription for ${name}?`)) { await axios.delete(`${API}/admin/subscriptions/${subId}`, { withCredentials: true }); fetchSubs(); } };
  const cleanOrphaned = async () => {
    if (window.confirm('Remove all subscriptions where the user no longer exists?')) {
      try {
        const { data } = await axios.post(`${API}/admin/subscriptions/clean-orphaned`, {}, { withCredentials: true });
        alert(`Cleaned ${data.cleaned} orphaned subscriptions.`);
        fetchSubs();
      } catch (err) { alert('Failed to clean orphaned subscriptions'); }
    }
  };

  const filtered = filterType === 'all' ? subs : subs.filter(s => (s.sub_type || (s.provider_id ? 'provider' : 'member')) === filterType);
  const memberCount = subs.filter(s => s.sub_type === 'member').length;
  const providerCount = subs.filter(s => s.sub_type === 'provider' || (!s.sub_type && s.provider_id)).length;
  const getType = (s) => s.sub_type || (s.provider_id ? 'provider' : 'member');

  return (
    <div data-testid="admin-subscriptions-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg text-white/80" style={{ fontFamily: 'Cormorant Garamond' }}>Subscriptions</h3>
        <button data-testid="clean-orphaned-btn" onClick={cleanOrphaned} className="text-xs border border-red-400/30 text-red-400 px-3 py-1.5 hover:bg-red-400/10 transition-colors">Clean Orphaned Records</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#0F0F14] border border-white/[0.04] p-4"><div className="text-xs text-white/40 mb-1">Member Subscriptions</div><div className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond' }}>{memberCount}</div><div className="text-[#C5A059] text-xs">{'\u20B9'}399/month</div></div>
        <div className="bg-[#0F0F14] border border-white/[0.04] p-4"><div className="text-xs text-white/40 mb-1">Provider Subscriptions</div><div className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond' }}>{providerCount}</div><div className="text-[#C5A059] text-xs">{'\u20B9'}799/month</div></div>
        <div className="bg-[#0F0F14] border border-white/[0.04] p-4"><div className="text-xs text-white/40 mb-1">Active</div><div className="text-2xl text-green-400 font-light" style={{ fontFamily: 'Cormorant Garamond' }}>{subs.filter(s => s.status === 'active').length}</div><div className="text-white/40 text-xs">of {subs.length} total</div></div>
      </div>
      <div className="flex gap-3 mb-4">
        {[{id:'all',label:'All'},{id:'member',label:'Members (\u20B9399)'},{id:'provider',label:'Providers (\u20B9799)'}].map(t => (
          <button key={t.id} data-testid={`sub-filter-${t.id}`} onClick={() => setFilterType(t.id)} className={`px-4 py-2 text-xs font-medium tracking-wide transition-colors ${filterType === t.id ? 'bg-[#C5A059] text-black' : 'border border-white/20 text-white hover:border-white/60'}`}>{t.label}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="subscriptions-table">
          <thead><tr className="border-b border-white/[0.08]"><th className="text-left py-3 text-white/60 font-medium">Type</th><th className="text-left py-3 text-white/60 font-medium">Name</th><th className="text-left py-3 text-white/60 font-medium">Amount</th><th className="text-left py-3 text-white/60 font-medium">Month/Year</th><th className="text-left py-3 text-white/60 font-medium">Status</th><th className="text-left py-3 text-white/60 font-medium">Payment ID</th><th className="text-left py-3 text-white/60 font-medium">Actions</th></tr></thead>
          <tbody>
            {filtered.map((s, i) => (
              <tr key={s.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`sub-row-${i}`}>
                <td className="py-3"><span className={`text-xs px-2 py-1 ${getType(s) === 'member' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>{getType(s) === 'member' ? 'Member' : 'Provider'}</span></td>
                <td className="py-3 text-white">{s.user_name || s.provider_name}</td>
                <td className="py-3 text-white/60">{'\u20B9'}{s.amount} {s.currency}</td>
                <td className="py-3 text-white/60">{s.month}/{s.year}</td>
                <td className="py-3"><span className={`text-xs px-2 py-1 ${s.status === 'active' ? 'bg-green-500/20 text-green-400' : s.status === 'expired' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{s.status}</span></td>
                <td className="py-3 text-white/40 text-xs font-mono">{s.payment_id || '\u2014'}</td>
                <td className="py-3 flex gap-2">
                  {s.status === 'pending' && <button data-testid={`sub-pay-${i}`} onClick={() => markPaid(s.id)} disabled={processing === s.id} className="text-[#C5A059] text-xs hover:text-[#E2C382] disabled:opacity-50">{processing === s.id ? 'Processing...' : 'Mark Paid'}</button>}
                  {s.status === 'active' && <button data-testid={`sub-expire-${i}`} onClick={() => updateStatus(s.id, 'expired')} className="text-red-400 text-xs hover:text-red-300">Expire</button>}
                  {s.status === 'expired' && <button data-testid={`sub-reactivate-${i}`} onClick={() => updateStatus(s.id, 'pending')} className="text-[#C5A059] text-xs hover:text-[#E2C382]">Reactivate</button>}
                  <button data-testid={`sub-delete-${i}`} onClick={() => deleteSub(s.id, s.user_name || s.provider_name || 'Unknown')} className="text-red-400/60 text-xs hover:text-red-300">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && <p className="text-white/40 text-sm mt-4">No subscriptions found.</p>}
    </div>
  );
};

// ═══════════════════════════════════════════
// ADMIN FEEDBACKS
// ═══════════════════════════════════════════

export const AdminFeedbacks = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/feedbacks`, { withCredentials: true }).then(({ data }) => setFeedbacks(data)).catch(() => {}); }, []);

  const renderStars = (rating) => [...Array(5)].map((_, i) => <span key={`star-${i}`} className={`text-sm ${i < rating ? 'text-[#C5A059]' : 'text-white/20'}`}>&#9733;</span>);

  return (
    <div data-testid="admin-feedbacks-section">
      <h3 className="text-lg text-white/80 mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>Member Feedback ({feedbacks.length})</h3>
      {feedbacks.length === 0 ? <p className="text-white/40 text-sm">No feedback yet.</p> : (
        <div className="space-y-4" data-testid="feedbacks-list">
          {feedbacks.map((f, i) => (
            <div key={f.id} className="bg-[#0F0F14] border border-white/[0.04] p-6" data-testid={`feedback-item-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <div><div className="text-white text-sm font-medium">Provider: {f.provider_name}</div><div className="text-white/40 text-xs">By: {f.member_name}</div></div>
                <div className="flex items-center gap-2"><div className="flex">{renderStars(f.star_rating)}</div><span className="text-white/60 text-xs">({f.star_rating}/5)</span></div>
              </div>
              <p className="text-white/60 text-sm mb-3">{f.comment}</p>
              <div className="flex items-center gap-4 text-xs">
                <span className={`px-2 py-1 ${f.would_recommend ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{f.would_recommend ? 'Would Recommend' : 'Would Not Recommend'}</span>
                <span className="text-white/40">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// ADMIN ANALYTICS
// ═══════════════════════════════════════════

export const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get(`${API}/admin/analytics`, { withCredentials: true }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <p className="text-white/60">Loading analytics...</p>;
  if (!data) return <p className="text-white/40">No analytics data available yet.</p>;

  return (
    <div data-testid="admin-analytics-section">
      <h3 className="text-lg text-white/80 mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>Member Access Analytics</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6"><div className="text-xs text-white/40 mb-1">Total Provider Views</div><div className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond' }}>{data.total_views}</div></div>
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6"><div className="text-xs text-white/40 mb-1">Categories Accessed</div><div className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond' }}>{data.by_category?.length || 0}</div></div>
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6"><div className="text-xs text-white/40 mb-1">Cities Searched</div><div className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond' }}>{data.by_city?.length || 0}</div></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6" data-testid="analytics-by-category">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Access by Service Category</div>
          {data.by_category?.length > 0 ? <div className="space-y-3">{data.by_category.map((c, i) => <div key={c._id || `cat-${i}`} className="flex justify-between items-center py-2 border-b border-white/[0.04]"><span className="text-white text-sm">{c.category_name || 'All Categories'}</span><span className="text-[#C5A059] text-sm font-medium">{c.count} views</span></div>)}</div> : <p className="text-white/40 text-sm">No data yet</p>}
        </div>
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6" data-testid="analytics-by-city">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Access by City Filter</div>
          {data.by_city?.length > 0 ? <div className="space-y-3">{data.by_city.map((c, i) => <div key={c._id || `city-${i}`} className="flex justify-between items-center py-2 border-b border-white/[0.04]"><span className="text-white text-sm">{c._id}</span><span className="text-[#C5A059] text-sm font-medium">{c.count} searches</span></div>)}</div> : <p className="text-white/40 text-sm">No data yet</p>}
        </div>
      </div>
      <div className="bg-[#0F0F14] border border-white/[0.04] p-6 mt-6" data-testid="analytics-by-user">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Top Members by Access Count</div>
        {data.by_user?.length > 0 ? <table className="w-full text-sm"><thead><tr className="border-b border-white/[0.08]"><th className="text-left py-2 text-white/60 font-medium">Member</th><th className="text-left py-2 text-white/60 font-medium">Views</th></tr></thead><tbody>{data.by_user.map((u, i) => <tr key={u.user_id || `user-${i}`} className="border-b border-white/[0.04]"><td className="py-2 text-white">{u.user_name}</td><td className="py-2 text-[#C5A059]">{u.access_count}</td></tr>)}</tbody></table> : <p className="text-white/40 text-sm">No data yet</p>}
      </div>
    </div>
  );
};
